import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Container, Form } from "react-bootstrap";
import BackButton from "../../../../components/UI_Kit/BackButton/BackButton";
import "../../styles.css";
import ViewpointFlow from "./ViewpointFlow"; // Import the ViewpointFlow component
import ConfirmationModal from "../../../../components/Modals/ConfirmationModal";

// Import api at the top of the file
import api from "../../../../services/api";

// Helper function to convert File objects or Blob URLs to Base64
const urlToBase64 = async (urlOrFile) => {
  if (urlOrFile instanceof File) {
    // Handle File object
    // Check if it's a HEIC file
    if (urlOrFile.name && urlOrFile.name.toLowerCase().endsWith(".heic")) {
      // For HEIC files, we still convert to base64 but need to handle metadata differently
      try {
        // If heic-convert is available, you could use it here to convert to JPEG
        // For now, we'll just convert to base64 as is
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(urlOrFile);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      } catch (error) {
        console.error("Error processing HEIC file:", error);
        return null;
      }
    }
    // Handle other file types normally
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(urlOrFile);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  } else if (typeof urlOrFile === "string" && urlOrFile.startsWith("blob:")) {
    // Handle Blob URL
    try {
      const response = await fetch(urlOrFile);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error fetching or converting blob URL:", error);
      return null; // Indicate failure
    }
  } else if (
    typeof urlOrFile === "string" &&
    urlOrFile.startsWith("data:image")
  ) {
    // Handle existing Base64 data URL
    return Promise.resolve(urlOrFile);
  } else {
    // Handle other cases (e.g., regular server URLs, null, undefined)
    // For regular URLs, we might not want/need to convert them here
    console.warn(
      "Source is not a File, Blob URL, or Data URL. Cannot convert to Base64:",
      urlOrFile
    );
    return Promise.resolve(null); // Resolve with null if conversion isn't applicable/possible
  }
};

const PointsInfo = () => {
  // ========== STATE MANAGEMENT ==========
  const [showViewpointFlow, setShowViewpointFlow] = useState(false);
  const [isDraftClicked, setIsDraftClicked] = useState(false);
  const [isQuitClicked, setIsQuitClicked] = useState(false);
  const [quickPointName, setQuickPointName] = useState("");
  const [collectedPoints, setCollectedPoints] = useState([]);
  const [activeDropdownIndex, setActiveDropdownIndex] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate(); // Add this line to use navigation
  const [isSubmitting, setIsSubmitting] = useState(false); // Add this state
  const [showBackModal, setShowBackModal] = useState(false); // Ajout de l'état pour la modale

  const draftClickedRef = useRef(false);
  const QuitClickedRef = useRef(false);

  const handleDraftClick = () => {
    draftClickedRef.current = true; // Set ref value immediately
    QuitClickedRef.current = false;
    console.log("Draft clicked:", draftClickedRef.current); // Will log true right away
    console.log("Quit clicked:", QuitClickedRef.current); // Will log true right away
    setIsDraftClicked(true);
  };

  const handleQuitClick = () => {
    QuitClickedRef.current = true; // Set ref value immediately
    draftClickedRef.current = false;
    console.log("Quit clicked:", QuitClickedRef.current); // Will log true right away
    console.log("Draft clicked:", draftClickedRef.current); // Will log true right away
    setIsQuitClicked(true);
  };

  // Update the handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Process each point for API submission
      for (const point of collectedPoints) {
        let apiLocalisations = []; // Initialize as empty

        let manualLat = null;
        let manualLon = null;

        // 1. Check for manually entered GPS coordinates
        if (point.gpsCoordinatesString) {
          const gpsString = point.gpsCoordinatesString.trim();
          // Regex to match formats like: 48.8584° N, 2.2945° E or 48.8584, 2.2945
          const dmsRegex =
            /(-?\d+(\.\d+)?)\s?°?\s?([NS])?,\s?(-?\d+(\.\d+)?)\s?°?\s?([EW])?/i;
          const simpleRegex = /(-?\d+(\.\d+)?),\s?(-?\d+(\.\d+)?)/;

          let match = gpsString.match(dmsRegex);
          if (match) {
            let lat = parseFloat(match[1]);
            const latDir = match[3];
            let lon = parseFloat(match[4]);
            const lonDir = match[6];

            if (!isNaN(lat) && !isNaN(lon)) {
              // Adjust sign based on direction
              if (latDir && latDir.toUpperCase() === "S") {
                lat = -Math.abs(lat);
              } else {
                lat = Math.abs(lat); // Ensure N is positive
              }
              if (lonDir && lonDir.toUpperCase() === "W") {
                lon = -Math.abs(lon);
              } else {
                lon = Math.abs(lon); // Ensure E is positive
              }
              manualLat = lat;
              manualLon = lon;
            } else {
              console.warn(
                "Could not parse numbers from DMS format:",
                gpsString
              );
            }
          } else {
            // Try simple format if DMS fails
            match = gpsString.match(simpleRegex);
            if (match) {
              const lat = parseFloat(match[1]);
              const lon = parseFloat(match[3]);
              if (!isNaN(lat) && !isNaN(lon)) {
                manualLat = lat;
                manualLon = lon;
              } else {
                console.warn(
                  "Could not parse numbers from simple format:",
                  gpsString
                );
              }
            } else {
              console.warn("Invalid GPS coordinate format:", gpsString);
            }
          }
        }
        // 2. Process photos for base64 encoding using the helper function
        let photoBase64Data = [];
        if (point.photos && point.photos.length > 0) {
          photoBase64Data = await Promise.all(
            point.photos.map(async (photo) => {
              let base64String = null; // Initialize as null
              let parsedLat = null;
              let parsedLon = null;
              let exifLat = null;
              let exifLon = null;

              // Determine the source for Base64 conversion
              const source = photo.file || photo.url; // Prioritize file, then URL

              // Check if this is a HEIC file
              const isHeicFile =
                source instanceof File &&
                source.name &&
                source.name.toLowerCase().endsWith(".heic");

              if (source) {
                base64String = await urlToBase64(source); // Use the helper function

                // For HEIC files, we need to extract metadata differently
                if (isHeicFile && photo.exifData) {
                  // If exifData is available from the component that handled the file upload
                  exifLat = photo.exifData.latitude;
                  exifLon = photo.exifData.longitude;
                }
              } else {
                console.warn("Photo object has neither file nor url:", photo);
              }

              // Try parsing the photo.location string FIRST
              if (typeof photo.location === "string") {
                // Simple Lat, Lon parsing (assuming format like "latitude, longitude")
                const parts = photo.location.split(",");
                if (parts.length === 2) {
                  parsedLat = parseFloat(parts[0].trim());
                  parsedLon = parseFloat(parts[1].trim());
                  if (isNaN(parsedLat) || isNaN(parsedLon)) {
                    console.warn(
                      "Failed to parse location string:",
                      photo.location
                    );
                    parsedLat = null; // Reset if parsing failed
                    parsedLon = null;
                  }
                } else {
                  // Attempt DMS parsing if simple parsing fails or isn't the format
                  // Example: "48.8584° N, 2.2945° E"
                  const dmsRegex =
                    /(-?\d+(\.\d+)?)\s?°?\s?([NS])?,\s?(-?\d+(\.\d+)?)\s?°?\s?([EW])?/i;
                  const match = photo.location.match(dmsRegex);
                  if (match) {
                    let lat = parseFloat(match[1]);
                    const latDir = match[3];
                    let lon = parseFloat(match[4]);
                    const lonDir = match[6];
                    if (!isNaN(lat) && !isNaN(lon)) {
                      if (latDir && latDir.toUpperCase() === "S")
                        lat = -Math.abs(lat);
                      else lat = Math.abs(lat);
                      if (lonDir && lonDir.toUpperCase() === "W")
                        lon = -Math.abs(lon);
                      else lon = Math.abs(lon);
                      parsedLat = lat;
                      parsedLon = lon;
                    } else {
                      console.warn(
                        "Could not parse numbers from DMS format in location string:",
                        photo.location
                      );
                    }
                  } else {
                    console.warn(
                      "Unrecognized location string format:",
                      photo.location
                    );
                  }
                }
              } else if (photo.location && typeof photo.location === "object") {
                // Assume EXIF data is in photo.location object if it's not a string
                exifLat = photo.location.latitude;
                exifLon = photo.location.longitude;
              }

              // Also check if lat/lon are directly on the photo object (from edit mapping)
              parsedLat = parsedLat ?? photo.latitude ?? null;
              parsedLon = parsedLon ?? photo.longitude ?? null;

              // Return null only if we have neither coordinates nor image data
              // This prevents sending empty localisation objects for photos without coords/files
              if (
                parsedLat === null &&
                parsedLon === null &&
                exifLat === null &&
                exifLon === null &&
                !base64String
              ) {
                return null;
              }

              return {
                base64: base64String, // Use the result from urlToBase64
                parsedLat: parsedLat,
                parsedLon: parsedLon,
                exifLat: exifLat,
                exifLon: exifLon,
              };
            })
          );
          // Filter out null entries (skipped photos)
          photoBase64Data = photoBase64Data.filter((p) => p !== null);
        }

        // 3. Construct apiLocalisations based on manual coords or photos
        if (manualLat !== null && manualLon !== null) {
          // Use manual coordinates. Associate with the first photo if available, otherwise just coords.
          apiLocalisations.push({
            latitude: manualLat,
            longitude: manualLon,
            // CHANGE KEY HERE: image_base64 -> image_path
            image_path:
              photoBase64Data.length > 0 ? photoBase64Data[0].base64 || "" : "", // Use first photo's base64 or empty
          });
          // If there are more photos, add them without coordinates (or decide how to handle)
          if (photoBase64Data.length > 1) {
            photoBase64Data.slice(1).forEach((photoData) => {
              apiLocalisations.push({
                latitude: 0, // Or maybe copy manual coords? Decide API requirement.
                longitude: 0,
                // CHANGE KEY HERE: image_base64 -> image_path
                image_path: photoData.base64 || "", // Use base64 or empty string
              });
            });
          }
        } else if (photoBase64Data.length > 0) {
          // No manual coords, use photo data
          apiLocalisations = photoBase64Data.map((photoData) => ({
            // Prioritize parsed string coords, then EXIF, then default 0
            latitude: photoData.parsedLat ?? photoData.exifLat ?? 0,
            longitude: photoData.parsedLon ?? photoData.exifLon ?? 0,
            // CHANGE KEY HERE: image_base64 -> image_path
            image_path: photoData.base64 || "", // Send base64 if available, else empty string
          }));
        } else {
          // If the API requires at least one localisation, you might send a default one:
          apiLocalisations.push({ latitude: 0, longitude: 0, image_path: "" });
        }

        // Prepare the rest of the point data for the API
        const pointPayload = {
          title: point.pointName,
          type: point.pointType, // Ensure this is the API type like 'vision_point'
          support_type: point.supportType,
          connection_type: point.installationType,
          power_type: point.powerSupplyType,
          comment: point.comment || "",
          // Adjust has_camera based on the mapped cameras array below
          has_camera: point.cameras && point.cameras.length > 0,
          // Include specific fields based on point type
          ...(point.pointType === "relay_point" &&
            {
              /* relay specific fields if any */
            }),
          ...(point.pointType === "high_point" && {
            coverage_area: point.coverageArea,
          }),
          ...(point.pointType === "surveillance_center" && {
            recorder_size: point.recorderSize,
            screen_size: point.screenSize,
          }),
          localisations: apiLocalisations, // Send the processed localisations
          // Map cameras array to match API expectation (rename viewingArea to vision_area)
          cameras: point.cameras
            ? point.cameras.map((camera) => ({
                name: camera.name || "",
                role: camera.role || "",
                vision_area: camera.viewingArea || "", // Map viewingArea to vision_area
                // Add comment if it exists in your camera object, otherwise omit or set default
                comment: camera.comment || "",
              }))
            : [], // Send empty array if no cameras
        };

        // Retrieve quotationId from localStorage (assuming it's stored there)
        const quotationId = window.location.pathname.split("/").pop();
        if (!quotationId) {
          throw new Error("Quotation ID not found in localStorage.");
        }

        // Get token
        const token = localStorage.getItem("access_token");
        if (!token) {
          throw new Error("Authentication token not found.");
        }

        // Make the API call to add the point to the quotation
        await api.post(`/quotations/${quotationId}/point`, pointPayload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json", // Ensure correct content type
          },
        });
      }

      // Optionally clear collected points or navigate
      setCollectedPoints([]); // Clear points after submission

      if (draftClickedRef.current === true) {
        // Check if 'Enregistrer comme brouillon' is clicked
        navigate(
          `/invoice/create/step1/${window.location.pathname.split("/").pop()}`
        );
      } else if (QuitClickedRef.current === true) {
        navigate("/");
      } else if (collectedPoints.length > 0) {
        navigate(
          `/invoice/create/step3/${window.location.pathname.split("/").pop()}`
        ); // Navigate to next step if points exist
      }
    } catch (error) {
      console.error("Error submitting points:", error);
      // Handle specific errors (e.g., network error, server error)
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred.";
      toast.error(`Failed to submit points: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========== EFFECTS ==========
  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdownIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ========== NEW EVENT HANDLERS ==========
  const handleEditPoint = (index) => {
    // const pointToEdit = collectedPoints[index];
    setShowViewpointFlow(true);
    setActiveDropdownIndex(index);
  };

  const handleDeletePoint = (index) => {
    setCollectedPoints((prev) => prev.filter((_, i) => i !== index));
    setActiveDropdownIndex(null);
  };

  const toggleDropdown = (index) => {
    setActiveDropdownIndex(activeDropdownIndex === index ? null : index);
  };

  const handleAddPoint = () => {
    setShowViewpointFlow(true);
  };

  const handleBackToAddPoint = (
    pointData,
    isEdit = false,
    editIndex = null
  ) => {
    setShowViewpointFlow(false);
    setQuickPointName(""); // Clear quick add name if used
    if (pointData) {
      const pointToAddOrUpdate = {
        ...pointData,
      };

      if (isEdit && editIndex !== null) {
        setCollectedPoints((prev) =>
          prev.map((p, i) => (i === editIndex ? pointToAddOrUpdate : p))
        );
      } else {
        setCollectedPoints((prev) => [...prev, pointToAddOrUpdate]);
      }
    }
    // Reset active dropdown if needed
    setActiveDropdownIndex(null);
  };

  // Fonction pour gérer le clic sur le bouton retour
  const handleBackButtonClick = () => {
    // Afficher la modale dans tous les cas
    handleDraftClick();
    setShowBackModal(true);
  };

  const handleQuitButtonClick = () => {
    // Afficher la modale dans tous les cas
    handleQuitClick();
    setShowBackModal(true);
  };

  // ========== RENDER LOGIC ==========
  return (
    <HelmetProvider>
      {/* Global notification container */}
      <ToastContainer />
      {/* SEO Metadata */}
      <Helmet>
        <title>Créer un devis</title>
        <meta name="description" content="Formulaire de création de devis" />
      </Helmet>

      {/* Main content container */}
      <Container className="pt-4 d-flex justify-content-center">
        <div className="container-wrap w-100">
          {/* Conditional rendering based on flow state */}
          {!showViewpointFlow ? (
            <>
              {/* ========== HEADER SECTION ========== */}
              <div className="header d-flex  justify-content-between">
                <span>
                  <BackButton onClick={handleBackButtonClick} />
                </span>
                <span>Créer un devis</span>
                <img src="/close-btn.png" onClick={handleQuitButtonClick} />
              </div>

              {/* ========== STEP PROGRESS INDICATOR ========== */}
              <div className="step_bar d-flex justify-content-between align-items-center w-100 mb-4">
                <img
                  className="step-image img-fluid"
                  src="/step_done.png"
                  alt="step1"
                />
                <img
                  className="step-image img-fluid"
                  src="/step_done.png"
                  alt="step2"
                />
                <img
                  className="step-image img-fluid"
                  src="/step.png"
                  alt="step3"
                />
              </div>

              {/* ========== MAIN FORM CONTENT ========== */}
              <Form onSubmit={handleSubmit}>
                <p className="form-title mb-4">Configuration des points</p>
                {/* Add Point Button */}
                <div className="add-point-wrapper mb-4">
                  <Link
                    onClick={handleAddPoint}
                    className="add-point d-flex align-items-center"
                  >
                    <img src="/plus.png" alt="add point" className="me-0" />
                    Ajouter un point
                  </Link>
                </div>
                {/* ========== POINTS LIST SECTION ========== */}
                {collectedPoints.length > 0 && (
                  <div className="mb-4 points-list">
                    {collectedPoints.map((point, index) => {
                      const typeCounts = {};
                      collectedPoints.slice(0, index).forEach((p) => {
                        const type = getPointDisplayName(p.pointType);
                        typeCounts[type] = (typeCounts[type] || 0) + 1;
                      });
                      const currentTypeDisplayName = getPointDisplayName(
                        point.pointType
                      );
                      const count = (typeCounts[currentTypeDisplayName] || 0) + 1;

                      return (
                        <div
                          key={index}
                          className="point-item d-flex justify-content-between align-items-center"
                        >
                          <div className="point-info ">
                            <div className="point-type mb-2">
                              {currentTypeDisplayName}{" "}
                              {collectedPoints.filter(
                                (p) =>
                                  getPointDisplayName(p.pointType) ===
                                  currentTypeDisplayName
                              ).length > 1
                                ? count
                                : ""}
                            </div>
                            <div className="point-name">
                              {point.pointName ||
                                point.name ||
                                "Unnamed Point"}
                            </div>
                          </div>
                          <div className="photo-actions">
                            <span
                              className="photo-actions-icon"
                              alt="actions"
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDropdown(index);
                              }}
                            >
                              ⋮
                            </span>
                            {activeDropdownIndex === index && (
                              <div
                                className="custom-dropdown-menu"
                                ref={dropdownRef}
                              >
                                <button
                                  className="custom-dropdown-item"
                                  onClick={() => handleEditPoint(index)}
                                >
                                  <img
                                    src="/edit_alt.png"
                                    className="action-icon"
                                    alt="edit"
                                  ></img>{" "}
                                  Modifier
                                </button>
                                <button
                                  className="custom-dropdown-item"
                                  onClick={() => handleDeletePoint(index)}
                                >
                                  <img
                                    src="/close.png"
                                    className="action-icon"
                                    alt="close"
                                  ></img>{" "}
                                  Supprimer
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* ========== FOOTER SECTION ========== */}
                <div className="footer shadow-lg d-flex justify-content-center">
                  <button
                    className="w-100 primary-button primary-button-block"
                    type="submit"
                    disabled={collectedPoints.length === 0 || isSubmitting}
                  >
                    {isSubmitting
                      ? "Envoi en cours..."
                      : "Voir le récapitulatif"}
                  </button>
                </div>
              </Form>
            </>
          ) : (
            /* ========== VIEWPOINT FLOW SECTION ========== */
            <ViewpointFlow
              initialData={
                activeDropdownIndex !== null
                  ? {
                      ...collectedPoints[activeDropdownIndex],
                      // Ensure all form fields have values (not just empty strings)
                      supportType:
                        collectedPoints[activeDropdownIndex].supportType ||
                        null,
                      installationType:
                        collectedPoints[activeDropdownIndex].installationType ||
                        null,
                      powerSupplyType:
                        collectedPoints[activeDropdownIndex].powerSupplyType ||
                        null,
                      // Keep other fields as is
                      pointName:
                        collectedPoints[activeDropdownIndex].pointName || "",
                      pointType:
                        collectedPoints[activeDropdownIndex].pointType || "",
                      photos: collectedPoints[activeDropdownIndex].photos || [],
                      comment:
                        collectedPoints[activeDropdownIndex].comment || "",
                      hasCameras:
                        collectedPoints[activeDropdownIndex].hasCameras || "",
                      coverageArea:
                        collectedPoints[activeDropdownIndex].coverageArea || "",
                      recorderSize:
                        collectedPoints[activeDropdownIndex].recorderSize || "",
                      screenSize:
                        collectedPoints[activeDropdownIndex].screenSize || "",
                    }
                  : { pointName: quickPointName }
              }
              onComplete={(data) =>
                handleBackToAddPoint(
                  data,
                  activeDropdownIndex !== null,
                  activeDropdownIndex
                )
              }
            />
          )}
        </div>
      </Container>
      {/* ========== CONFIRMATION MODAL SECTION ========== */}

      {/* Modale de confirmation pour le bouton retour */}
      <ConfirmationModal
        show={showBackModal}
        handleClose={() => setShowBackModal(false)}
        handleConfirm={() => {
          if (collectedPoints.length === 0 && QuitClickedRef.current === false) {
            navigate(
              `/invoice/create/step1/${window.location.pathname
                .split("/")
                .pop()}`
            );
          } else {
            handleSubmit({ preventDefault: () => {} });
          }
        }}
        title="Vous n'avez pas terminé de compléter votre devis"
        isSubmitting={false}
        buttonTitle="Enregistrer comme brouillon"
        LinkTitle="Quitter sans enregistrer"
      >
        <p className="text-center">
          Vous êtes sur le point de quitter sans avoir terminé votre devis.
          Êtes-vous sûr de vouloir quitter ? <br />
          Sinon, vous pouvez l'enregistrer en brouillon pour le retrouver plus
          tard.
        </p>
      </ConfirmationModal>

    </HelmetProvider>
  );
};

export default PointsInfo;


  const getPointDisplayName = (pointType) => {
    switch (pointType) {
      case "vision_point":
        return "Point de vision";
      case "relay_point":
        return "Point relais";
      case "high_point":
        return "Point haut";
      case "surveillance_center":
        return "Centre de surveillance";
      default:
        return "Unknown Type";
    }
  };
